import React, { useState } from 'react';
import { X, Search, Film, Image, FileText } from 'lucide-react';
import { Asset } from '../types/assets';

interface AssetSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (asset: Asset) => void;
  assets: Asset[];
}

const AssetSelectorModal = ({ isOpen, onClose, onSelect, assets }: AssetSelectorModalProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<string>('all');

  if (!isOpen) return null;

  const getAssetIcon = (fileType: string) => {
    switch (fileType.toLowerCase()) {
      case 'video':
        return <Film className="w-6 h-6 text-blue-500" />;
      case 'image':
        return <Image className="w-6 h-6 text-green-500" />;
      default:
        return <FileText className="w-6 h-6 text-gray-500" />;
    }
  };

  const formatFileSize = (bytes: number) => {
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const filteredAssets = assets
    .filter(asset => {
      const matchesSearch = asset.fileName.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter = filter === 'all' || asset.fileType.toLowerCase() === filter;
      return matchesSearch && matchesFilter;
    });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-semibold">Select Asset</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Search and Filters */}
        <div className="p-4 border-b">
          <div className="flex gap-4 mb-4">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search assets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-lg ${
                  filter === 'all' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilter('video')}
                className={`px-4 py-2 rounded-lg ${
                  filter === 'video' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Videos
              </button>
              <button
                onClick={() => setFilter('image')}
                className={`px-4 py-2 rounded-lg ${
                  filter === 'image' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Images
              </button>
            </div>
          </div>
        </div>

        {/* Asset List */}
        <div className="flex-1 overflow-auto p-4">
          <div className="grid grid-cols-2 gap-4">
            {filteredAssets.map((asset) => (
              <div
                key={asset.assetId}
                onClick={() => onSelect(asset)}
                className="flex items-start p-4 border rounded-lg hover:bg-gray-50 cursor-pointer"
              >
                <div className="mr-4">
                  {getAssetIcon(asset.fileType)}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-gray-900 truncate">
                    {asset.fileName}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {asset.fileFormat.toUpperCase()} • {formatFileSize(asset.fileSize)}
                  </p>
                  {asset.duration && (
                    <p className="text-sm text-gray-500">
                      Duration: {formatDuration(asset.duration)}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
          {filteredAssets.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No assets found matching your criteria
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 mr-2"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetSelectorModal;